import { type HistoricalEventResponse } from '@solinkcorp/api-types';
import React, { createContext, useContext } from 'react';
import { type ServiceDisplayDayPart } from '../day-part/utils';
import { type ServiceDisplayLayout, type ServiceDisplayWidget } from '../layout/types';

export interface SDMClient {
  getServiceDisplayLayouts<T = ServiceDisplayLayout>(): Promise<T[]>;
  getCustomerDefaultSdmLayout(customerId: string): Promise<{ defaultSdmLayoutId: string }>;
  createServiceDisplayLayout<T extends ServiceDisplayLayout = ServiceDisplayLayout>(
    data: Pick<T, 'customerId' | 'name'>,
  ): Promise<T>;
  updateServiceDisplayLayout(
    id: string,
    data: Partial<Pick<ServiceDisplayLayout, 'name'>>,
  ): Promise<ServiceDisplayLayout>;
  deleteServiceDisplayLayout(id: string): Promise<void>;
  updateServiceDisplayWidgets(
    layoutId: string,
    data: { widgets: ServiceDisplayWidget[] },
  ): Promise<{ widgets: ServiceDisplayWidget[] }>;
  deleteServiceDisplayWidgets(
    layoutId: string,
    widgetIds: string[],
  ): Promise<ServiceDisplayWidget[]>;
  bulkDeleteServiceDisplayDayPartThresholds(thresholdIds: string[]): Promise<unknown>;
  updateCustomerDefaultSdmLayout(
    customerId: string,
    defaultSdmLayoutId: string | null,
  ): Promise<{ defaultSdmLayoutId: string | null }>;
  getServiceDisplayDayParts<T = ServiceDisplayDayPart>(options?: {
    customerId?: string;
  }): Promise<T[]>;
  createServiceDisplayDayPart(data: {
    customerId: string;
    label: string;
    dayParts?: ServiceDisplayDayPart['dayParts'];
  }): Promise<ServiceDisplayDayPart>;
  updateServiceDisplayDayPart(
    id: string,
    data: Partial<Pick<ServiceDisplayDayPart, 'label' | 'dayParts'>>,
  ): Promise<ServiceDisplayDayPart>;
  deleteServiceDisplayDayPart(id: string): Promise<void>;
  getHistoricalEvents(
    deviceUrl: string,
    accessToken: string,
    start: number,
    end: number,
  ): Promise<HistoricalEventResponse[]>;
}

const SDMClientContext = createContext<SDMClient | null>(null);

export function useSDMClient(): SDMClient {
  const client = useContext(SDMClientContext);
  if (!client) {
    throw new Error('useSDMClient must be used within an SDMClientProvider');
  }
  return client;
}

export function SDMClientProvider({
  client,
  children,
}: {
  client: SDMClient;
  children: React.ReactNode;
}) {
  return <SDMClientContext.Provider value={client}>{children}</SDMClientContext.Provider>;
}
